import React, { useState, useEffect } from "react";
import { Spin, message } from "antd";
import { LogOut, Briefcase, Users, BarChart3 } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { authService } from "../services/auth.service";
import { Service } from "../types";
import { SERVICE_CONFIG } from "../config/constants";
import styles from "./ServiceSelectionPage.module.css";

const ServiceSelectionPage: React.FC = () => {
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    loadServices();
  }, []);

  const loadServices = async () => {
    try {
      const availableServices = await authService.getAvailableServices();
      setServices(availableServices);
    } catch (error: any) {
      message.error("Không thể tải danh sách dịch vụ");
      console.error("[ServiceSelectionPage] ❌ Load services error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleServiceClick = (service: Service) => {
    const token = authService.getToken();

    if (token) {
      // Đảm bảo service.url không có trailing slash và không có query params
      const baseUrl = service.url.replace(/\/$/, '').split('?')[0];
      const redirectUrl = `${baseUrl}?token=${encodeURIComponent(token)}`;
      window.location.href = redirectUrl;
    } else {
      console.error('[ServiceSelectionPage] ❌ No token found, cannot redirect');
      message.error("Token không hợp lệ. Vui lòng đăng nhập lại.");
      navigate("/login");
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/login");
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  const getServiceIcon = (serviceId: string) => {
    const iconProps = { size: 48, color: "#007bff" };
    const config =
      SERVICE_CONFIG[serviceId.toUpperCase() as keyof typeof SERVICE_CONFIG];

    if (!config) {
      return <Briefcase {...iconProps} />;
    }

    switch (config.icon) {
      case "Briefcase":
        return <Briefcase {...iconProps} />;
      case "Users":
        return <Users {...iconProps} />;
      case "BarChart3":
        return <BarChart3 {...iconProps} />;
      default:
        return <Briefcase {...iconProps} />;
    }
  };

  const getServiceDescription = (serviceId: string) => {
    switch (serviceId.toLowerCase()) {
      case "crm":
        return "Quản lý tương tác khách hàng, theo dõi cơ hội bán hàng và xây dựng mối quan hệ lâu dài.";
      case "pm":
        return "Lập kế hoạch, theo dõi và triển khai dự án hiệu quả với khả năng cộng tác toàn diện.";
      case "hrm":
        return "Quản lý nhân sự, lương thưởng và phát triển năng lực nhân viên.";
      case "mkt":
        return "Quản lý chiến dịch marketing, theo dõi hiệu suất quảng cáo và tối ưu ngân sách hiệu quả.";
      default:
        return "Nhấp để truy cập";
    }
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <Spin size="large" />
      </div>
    );
  }

  const displayName = user?.fullName || user?.email || "";
  const firstName =
    displayName.split(" ").filter(Boolean).slice(-1)[0] || displayName;
  const avatarLetter =
    displayName.trim().charAt(0).toUpperCase() || "?";

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.brand}>Hệ Thống Quản Lý BMA</div>
        <div className={styles.headerRight}>
          <div className={styles.userInfo}>
            {user?.avatar ? (
              <img
                src={user.avatar}
                alt={displayName}
                className={styles.userAvatarImage}
              />
            ) : (
              <div className={styles.userAvatar}>{avatarLetter}</div>
            )}
            <div>
              <h4 className={styles.userName}>
                {user?.fullName || user?.email}
              </h4>
              <p className={styles.userEmail}>{user?.email}</p>
            </div>
          </div>
          <button onClick={handleLogout} className={styles.logoutButton}>
            <LogOut size={18} />
            <span>Đăng xuất</span>
          </button>
        </div>
      </div>

      <div className={styles.content}>
        <h2 className={styles.title}>
          Xin chào, <span className={styles.highlight}>{firstName}</span>
        </h2>
        <p className={styles.subtitle}>
          Chọn mô-đun hệ thống để bắt đầu phiên làm việc của mình
        </p>

        {services.length === 0 ? (
          <div className={styles.emptyCard}>
            <p>Bạn chưa được cấp quyền truy cập dịch vụ nào.</p>
            <p>Vui lòng liên hệ quản trị viên để được cấp quyền.</p>
          </div>
        ) : (
          <div className={styles.serviceGrid}>
            {services.map((service) => (
              <div
                key={service.id}
                className={`${styles.serviceCard} ${
                  styles[`serviceCard-${service.id.toLowerCase()}`]
                }`}
                onClick={() => handleServiceClick(service)}
              >
                <div className={styles.serviceIcon}>
                  {getServiceIcon(service.id)}
                </div>
                <h4 className={styles.serviceName}>{service.name}</h4>
                <p className={styles.serviceDescription}>
                  {/* gọi hàm đã khai báo thay vì viết logic thủ công  */}
                  {getServiceDescription(service.id)}
                </p>
                <button className={styles.serviceLink}>Truy cập</button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ServiceSelectionPage;